import React, { useMemo } from "react";
import { Box } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Target,
  Task,
  TargetStatus,
  TargetType,
  SimpleTargetValue,
  PersonTargetValue,
} from "./types";
import {
  STATUS_COLUMN_ORDER,
  TYPE_CONFIG,
  TASK_STATUS_COLOR,
  STATUS_CONFIG,
} from "./constants";
import { StatusChips } from "./WorkspacePanel";
import { formatRelativeTime } from "../shared/helpers/formatRelativeTime";
import { tokens } from "../../theme/tokens";

export interface ObjectiveRow {
  target: Target;
  taskId: string;
  taskTitle: string;
  poiName: string;
  poiId: string;
  isPrimary: boolean;
}

export type ViewMode = "list" | "board" | "grouped";

export interface GroupedModeProps {
  rows: ObjectiveRow[];
  tasks: Task[];
  onJumpToTask: (taskId: string) => void;
}

export function getObjectiveDisplayText(target: Target): string {
  switch (target.type) {
    case "FIELD_RESOURCE":
      return target.label || "";
    case "PERSON":
      return (target.value as PersonTargetValue).name || (target.value as SimpleTargetValue).text || target.label || "";
    default:
      return (target.value as SimpleTargetValue).text || target.label || "";
  }
}

export const TypeIcon: React.FC<{ type: TargetType; status: TargetStatus }> = ({
  type,
  status,
}) => {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.OTHER;
  const color = STATUS_CONFIG[status]?.color ?? tokens.color.text.secondary;
  return <cfg.Icon sx={{ fontSize: "15px", color, flexShrink: 0 }} />;
};

export const TypeBadge: React.FC<{ type: TargetType }> = ({ type }) => {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.OTHER;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: "4px",
        backgroundColor: tokens.color.surface.subtle,
        color: tokens.color.text.secondary,
        border: `1px solid ${tokens.color.border.default}`,
        whiteSpace: "nowrap",
        flexShrink: 0,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {cfg.label}
    </span>
  );
};

export const EmptyMessage: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ px: "16px", py: "20px" }}>
    <span style={{ fontSize: "12px", color: tokens.color.border.strong }}>{message}</span>
  </Box>
);

export const ListMode: React.FC<{
  rows: ObjectiveRow[];
  onJumpToTask: (taskId: string) => void;
}> = ({ rows, onJumpToTask }) => {
  if (rows.length === 0)
    return <EmptyMessage message="No objectives match the active filters." />;

  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      {rows.map((row) => (
        <Box
          key={row.target.target_id}
          onClick={() => onJumpToTask(row.taskId)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            px: "14px",
            py: "9px",
            borderBottom: `1px solid ${tokens.color.border.default}`,
            "&:last-child": { borderBottom: "none" },
            cursor: "pointer",
            "&:hover": { backgroundColor: tokens.color.surface.sunken },
            "&:active": { backgroundColor: tokens.color.surface.subtle },
            minWidth: 0,
          }}
        >
          {/* Star */}
          <Box sx={{ flexShrink: 0 }}>
            {row.isPrimary ? (
              <StarIcon sx={{ fontSize: "14px", color: tokens.color.workflow.doing.fg }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: "14px", color: tokens.color.border.strong }} />
            )}
          </Box>

          {/* Type icon */}
          <TypeIcon type={row.target.type} status={row.target.status} />

          {/* Objective text */}
          <span
            style={{
              fontSize: "12px",
              fontWeight: row.isPrimary ? 600 : 400,
              color: tokens.color.text.primary,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getObjectiveDisplayText(row.target)}
          </span>

          {/* Type badge */}
          <TypeBadge type={row.target.type} />

          {/* Task name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              flexShrink: 0,
              maxWidth: "130px",
            }}
          >
            <OpenInNewIcon sx={{ fontSize: "10px", color: tokens.color.text.secondary }} />
            <span
              style={{
                fontSize: "11px",
                color: tokens.color.text.secondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.taskTitle}
            </span>
          </Box>

          {/* Relative time */}
          <span
            style={{
              fontSize: "11px",
              color: tokens.color.text.secondary,
              whiteSpace: "nowrap",
              flexShrink: 0,
              minWidth: "56px",
              textAlign: "right",
            }}
          >
            {formatRelativeTime(row.target.updated_at)}
          </span>

          {/* Status chips — stop propagation so clicks don't navigate */}
          <Box onClick={(e) => e.stopPropagation()}>
            <StatusChips target={row.target} taskId={row.taskId} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const BoardCard: React.FC<{
  row: ObjectiveRow;
  onJumpToTask: (taskId: string) => void;
}> = ({ row, onJumpToTask }) => (
  <Box
    sx={{
      backgroundColor: tokens.color.surface.default,
      border: `1px solid ${tokens.color.border.default}`,
      borderRadius: "8px",
      p: "10px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}
  >
    {/* Row 1: colored icon + title + star */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        minWidth: 0,
      }}
    >
      <TypeIcon type={row.target.type} status={row.target.status} />
      <span
        style={{
          fontSize: "12px",
          color: tokens.color.text.primary,
          fontWeight: row.isPrimary ? 600 : 400,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.4,
        }}
      >
        {getObjectiveDisplayText(row.target)}
      </span>
      {row.isPrimary && (
        <StarIcon sx={{ fontSize: "12px", color: tokens.color.workflow.doing.fg, flexShrink: 0 }} />
      )}
    </Box>

    {/* Row 2: type badge + jump-to-task button */}
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <TypeBadge type={row.target.type} />
      <button
        type="button"
        onClick={() => onJumpToTask(row.taskId)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          fontSize: "10px",
          color: tokens.color.text.secondary,
          backgroundColor: "transparent",
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: "4px",
          padding: "1px 6px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "130px",
        }}
      >
        <OpenInNewIcon style={{ fontSize: "10px", flexShrink: 0 }} />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.taskTitle}
        </span>
      </button>
    </Box>
  </Box>
);

export const BoardMode: React.FC<{
  rows: ObjectiveRow[];
  onJumpToTask: (taskId: string) => void;
}> = ({ rows, onJumpToTask }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      gap: "10px",
      overflowX: "auto",
      overflowY: "hidden",
      p: "12px",
      minHeight: 0,
    }}
  >
    {STATUS_COLUMN_ORDER.map((status) => {
      const cfg = STATUS_CONFIG[status];
      const colRows = rows.filter((r) => r.target.status === status);
      return (
        <Box
          key={status}
          sx={{
            flex: "1 1 200px",
            minWidth: "180px",
            maxWidth: "280px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "8px",
              py: "6px",
              backgroundColor: cfg.bg,
              borderRadius: "6px",
              border: `1px solid ${cfg.border}`,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                color: cfg.color,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {cfg.label}
            </span>
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: cfg.color,
                opacity: 0.7,
              }}
            >
              {colRows.length}
            </span>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              pb: "4px",
            }}
          >
            {colRows.length === 0 ? (
              <Box
                sx={{
                  py: "16px",
                  textAlign: "center",
                  border: `1px dashed ${tokens.color.border.default}`,
                  borderRadius: "6px",
                }}
              >
                <span style={{ fontSize: "11px", color: tokens.color.border.strong }}>
                  Empty
                </span>
              </Box>
            ) : (
              colRows.map((row) => (
                <BoardCard
                  key={row.target.target_id}
                  row={row}
                  onJumpToTask={onJumpToTask}
                />
              ))
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export const GroupedMode: React.FC<GroupedModeProps> = ({
  rows,
  tasks,
  onJumpToTask,
}) => {
  const groups = useMemo(() => {
    const rowsByTask = rows.reduce<Record<string, ObjectiveRow[]>>((acc, r) => {
      if (!acc[r.taskId]) acc[r.taskId] = [];
      acc[r.taskId].push(r);
      return acc;
    }, {});

    return Object.entries(rowsByTask).map(([taskId, taskRows]) => {
      const task = tasks.find((t) => t.task_id === taskId);
      const total = Number(task?.progress_total ?? 0);
      const done = Number(task?.progress_done ?? 0);
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      const progressColor =
        done === 0 ? tokens.color.border.strong : pct === 100 ? tokens.color.workflow.worked.fg : tokens.color.workflow.doing.fg;
      return {
        taskId,
        task,
        taskRows,
        total,
        done,
        pct,
        progressColor,
        poiName: taskRows[0]?.poiName ?? "",
      };
    });
  }, [rows, tasks]);

  if (groups.length === 0)
    return <EmptyMessage message="No objectives match the active filters." />;

  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      {groups.map(
        ({
          taskId,
          task,
          taskRows,
          total,
          done,
          pct,
          progressColor,
          poiName,
        }) => (
          <Box
            key={taskId}
            sx={{
              borderBottom: `1px solid ${tokens.color.border.default}`,
              "&:last-child": { borderBottom: "none" },
            }}
          >
            {/* Group header — clicking navigates to this task */}
            <Box
              onClick={() => onJumpToTask(taskId)}
              sx={{
                px: "16px",
                py: "10px",
                backgroundColor: tokens.color.surface.sunken,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                "&:hover": { backgroundColor: tokens.color.surface.subtle },
                transition: "background-color 0.12s ease",
              }}
            >
              {/* Left: status dot + title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "7px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  title={`Status: ${task?.status ?? "TODO"}`}
                  aria-label={`Status: ${task?.status ?? "TODO"}`}
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    backgroundColor: TASK_STATUS_COLOR[task?.status ?? "TODO"],
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: tokens.color.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {task?.title ?? taskId}
                </span>
              </Box>

              {/* Right: group label + count + progress bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                }}
              >
                {task?.group_label && (
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 600,
                      color: tokens.color.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {task.group_label}
                  </span>
                )}
                <span
                  style={{
                    fontSize: "10px",
                    color: tokens.color.text.secondary,
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {done}/{total} worked
                </span>
                {total > 0 && (
                  <Box
                    sx={{
                      width: "60px",
                      height: "3px",
                      background: tokens.color.surface.subtle,
                      borderRadius: 99,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${pct}%`,
                        background: progressColor,
                        borderRadius: 99,
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Objective rows — clickable, navigate to same task */}
            {taskRows.map((row) => (
              <Box
                key={row.target.target_id}
                onClick={() => onJumpToTask(row.taskId)}
                sx={{
                  px: "14px",
                  py: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: `1px solid ${tokens.color.border.default}`,
                  "&:last-child": { borderBottom: "none" },
                  cursor: "pointer",
                  "&:hover": { backgroundColor: tokens.color.surface.sunken },
                  "&:active": { backgroundColor: tokens.color.surface.subtle },
                  minWidth: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  {row.isPrimary ? (
                    <StarIcon sx={{ fontSize: "13px", color: tokens.color.workflow.doing.fg }} />
                  ) : (
                    <StarBorderIcon
                      sx={{ fontSize: "13px", color: tokens.color.border.default }}
                    />
                  )}
                </Box>
                <TypeIcon type={row.target.type} status={row.target.status} />
                <span
                  style={{
                    fontSize: "12px",
                    color: tokens.color.text.primary,
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: row.isPrimary ? 600 : 400,
                  }}
                >
                  {getObjectiveDisplayText(row.target)}
                </span>
                <TypeBadge type={row.target.type} />
                <Box onClick={(e) => e.stopPropagation()}>
                  <StatusChips target={row.target} taskId={row.taskId} />
                </Box>
              </Box>
            ))}
          </Box>
        ),
      )}
    </Box>
  );
};
